import { useEffect, useState } from "react";
import { Download, Plus, Search, User, Users, X } from "lucide-react";
import { api, money } from "@/lib/api";
import { Pagination, usePagination } from "@/components/Pagination";
import { ContactModal } from "@/components/ContactModal";

const TABS = [
  { v: "Customer", l: "Pelanggan", color: "#f59e0b" },
  { v: "Supplier", l: "Supplier", color: "#8b5cf6" },
  { v: "Employee", l: "Karyawan", color: "#10b981" },
  { v: "Other", l: "Lainnya", color: "#0ea5e9" },
  { v: "ALL", l: "Semua", color: "#64748b" },
];
const typeColor = (t) => TABS.find((x) => x.v === t)?.color || "#64748b";

export default function Contacts() {
  const [contacts, setContacts] = useState(null);
  const [overview, setOverview] = useState(null);
  const [tab, setTab] = useState("Customer");
  const [term, setTerm] = useState("");
  const [modal, setModal] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [message, setMessage] = useState(null);
  const load = () => {
    api("/contacts").then(setContacts).catch(() => {});
    api("/contacts/overview").then(setOverview).catch(() => {});
  };
  useEffect(load, []);
  const notify = (text, type = "success") => { setMessage({ text, type }); setTimeout(() => setMessage(null), 6000); };
  const primaryType = (c) => (c.types?.length ? c.types[0] : c.type) || "Other";
  const filtered = (contacts || [])
    .filter((c) => tab === "ALL" || primaryType(c) === tab)
    .filter((c) => !term || c.name.toLowerCase().includes(term.toLowerCase()) || (c.company || "").toLowerCase().includes(term.toLowerCase()));
  const pager = usePagination(filtered, 25, `${tab}|${term}`);
  if (!contacts) return <div className="report-loading" data-testid="contacts-loading">Memuat kontak...</div>;
  const saldo = (c) => {
    const t = primaryType(c);
    if (t === "Customer") return overview?.receivable_balances?.[c.name] || 0;
    if (t === "Supplier") return overview?.payable_balances?.[c.name] || 0;
    return (overview?.receivable_balances?.[c.name] || 0) + (overview?.payable_balances?.[c.name] || 0);
  };
  const cards = tab === "Customer" ? [
    { label: "Piutang belum dibayar", tone: "yellow", count: overview?.receivable?.open_count || 0, total: overview?.receivable?.open_total || 0 },
    { label: "Piutang jatuh tempo", tone: "red", count: overview?.receivable?.overdue_count || 0, total: overview?.receivable?.overdue_total || 0 },
    { label: "Kredit memo", tone: "blue", count: 0, total: 0 },
  ] : tab === "Supplier" ? [
    { label: "Hutang belum dibayar", tone: "yellow", count: overview?.payable?.open_count || 0, total: overview?.payable?.open_total || 0 },
    { label: "Hutang jatuh tempo", tone: "red", count: overview?.payable?.overdue_count || 0, total: overview?.payable?.overdue_total || 0 },
    { label: "Debit memo", tone: "blue", count: 0, total: 0 },
  ] : null;
  const exportCsv = () => {
    const header = "Nama panggilan;Tipe;Nama perusahaan;Alamat;Email;No. handphone;Saldo";
    const lines = filtered.map((c) => [c.name, TABS.find((t) => t.v === primaryType(c))?.l || "", c.company || "", c.billing_address || "", c.email || "", c.mobile || c.phone || "", saldo(c)].join(";"));
    const blob = new Blob(["\uFEFF" + [header, ...lines].join("\n")], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `kontak-${tab.toLowerCase()}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };
  const remove = async () => {
    try {
      await api(`/contacts/${deleting.id}`, { method: "DELETE" });
      notify(`Kontak "${deleting.name}" berhasil dihapus`);
      load();
    } catch (e) { notify(e.message, "error"); }
    setDeleting(null);
  };
  return (
    <div className="workflow-page" data-testid="contacts-page">
      <div className="page-heading">
        <div><p className="eyebrow">RUANG KERJA TRANSAKSI</p><h1>Kontak</h1><p className="muted">Kelola data, status, dan aktivitas kontak dari satu tempat.</p></div>
        <button data-testid="contacts-create-button" className="primary-btn" onClick={() => setModal({})}><Plus size={15} /> Buat Kontak</button>
      </div>
      {message && <div data-testid="contacts-message" className={`success-message ${message.type === "error" ? "error" : ""}`}>{message.text}<button onClick={() => setMessage(null)}><X size={14} /></button></div>}
      <div className="contact-tabs" data-testid="contact-tabs">
        {TABS.map((t) => (
          <button key={t.v} data-testid={`contact-tab-${t.v.toLowerCase()}`} className={`contact-tab ${tab === t.v ? "active" : ""}`} onClick={() => setTab(t.v)}>
            {t.v === "ALL" ? <Users size={14} style={{ color: t.color }} /> : <User size={14} style={{ color: t.color }} />} {t.l}
          </button>
        ))}
      </div>
      {cards && (
        <div className="contact-cards" data-testid="contact-summary-cards">
          {cards.map((c) => (
            <div key={c.label} className={`contact-card ${c.tone}`} data-testid={`contact-card-${c.tone}`}>
              <div className="cc-head"><span>{c.label}</span><b className="cc-badge">{c.count}</b></div>
              <small>Total</small>
              <strong>{money(c.total)}</strong>
            </div>
          ))}
        </div>
      )}
      <div className="workflow-toolbar surface prod-toolbar">
        <div className="search-box"><Search size={14} /><input data-testid="contacts-search" placeholder="Cari kontak..." value={term} onChange={(e) => setTerm(e.target.value)} /></div>
        <span className="record-count">{filtered.length} data</span>
        <div className="toolbar-spacer" />
        <button data-testid="contacts-export" className="secondary-btn small" onClick={exportCsv}><Download size={14} /> Ekspor</button>
      </div>
      <div className="surface workflow-table-wrap">
        <table className="workflow-table" data-testid="contacts-table">
          <thead><tr><th>NAMA PANGGILAN</th><th /><th>NAMA PERUSAHAAN</th><th>ALAMAT</th><th>EMAIL</th><th>NO. HANDPHONE</th><th className="right">SALDO</th><th /></tr></thead>
          <tbody>
            {pager.pageItems.map((c, i) => (
              <tr key={c.id} data-testid={`contact-row-${i}`} onClick={() => setModal(c)} className="clickable">
                <td className="primary-cell contact-name">{c.name}</td>
                <td><span className="type-dot" title={TABS.find((t) => t.v === primaryType(c))?.l} style={{ background: typeColor(primaryType(c)) }} /></td>
                <td>{c.company || "—"}</td>
                <td>{c.billing_address || "—"}</td>
                <td>{c.email || "—"}</td>
                <td>{c.mobile || c.phone || "—"}</td>
                <td className="right mono">{money(saldo(c))}</td>
                <td className="row-actions">
                  <button data-testid={`contact-edit-${i}`} className="table-action" onClick={(e) => { e.stopPropagation(); setModal(c); }}>Edit</button>
                  <button data-testid={`contact-delete-${i}`} className="table-action danger" onClick={(e) => { e.stopPropagation(); setDeleting(c); }}>Hapus</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <div className="empty-state" data-testid="contacts-empty"><strong>Belum ada kontak</strong><span>Klik "Buat Kontak" untuk menambahkan {TABS.find((t) => t.v === tab)?.l.toLowerCase()} baru.</span></div>}
        <Pagination pager={pager} testid="contacts-pagination" />
      </div>
      {modal && (
        <ContactModal initial={modal.id ? modal : null} defaultTypes={tab === "ALL" ? ["Customer"] : [tab]}
          onClose={() => setModal(null)}
          onSaved={(r) => { setModal(null); notify(`Kontak "${r.name}" berhasil disimpan`); load(); }} />
      )}
      {deleting && (
        <div className="modal-overlay" data-testid="contact-delete-modal" onClick={() => setDeleting(null)}>
          <div className="modal-panel narrow" onClick={(e) => e.stopPropagation()}>
            <div className="section-head">
              <div><p className="eyebrow">KONFIRMASI HAPUS</p><h2>Hapus Kontak?</h2></div>
              <button className="icon-btn" onClick={() => setDeleting(null)}><X size={18} /></button>
            </div>
            <p className="muted delete-warning">"{deleting.name}" akan dihapus permanen. Kontak yang sudah digunakan pada transaksi tidak dapat dihapus.</p>
            <div className="form-actions">
              <button data-testid="contact-delete-confirm" className="danger-btn" onClick={remove}>Ya, Hapus</button>
              <button data-testid="contact-delete-cancel" className="secondary-btn" onClick={() => setDeleting(null)}>Batal</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
